import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Shield, Users, Search, Video, Play, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import ListingCard from '../components/ListingCard';
import TradieCarousel from '../components/TradieCarousel';
import FeaturedCarousel from '../components/FeaturedCarousel';

import { API_URL } from '../utils/api';

const HomePage = () => {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [creatorPosts, setCreatorPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingsRes, categoriesRes, creatorRes] = await Promise.all([
          axios.get(`${API_URL}/api/listings?limit=8`),
          axios.get(`${API_URL}/api/categories`),
          axios.get(`${API_URL}/api/creator-feed?limit=4`)
        ]);
        setFeaturedListings(listingsRes.data);
        setCategories(categoriesRes.data.categories);
        setCreatorPosts(creatorRes.data.posts || []);
      } catch (err) {
        console.error('Failed to fetch data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categoryIcons = {
    'furniture': '🪑',
    'home-decor': '🏠',
    'art': '🎨',
    'ceramics': '🏺',
    'woodwork': '🪵',
    'textiles': '🧵',
    'jewelry': '💎',
    'vintage': '⏰',
    'outdoor': '🌿',
    'other': '📦'
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1542166498816-dcf3df2db47e?w=1920"
            alt="Artisan crafting"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAFAF9] via-[#FAFAF9]/90 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-2xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-800 tracking-tight leading-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Crafters Market
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-2 text-lg sm:text-xl text-teal-600 font-medium"
            >
              Australia&apos;s local marketplace for makers
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-base text-[#57534E] leading-relaxed"
            >
              Shop local handmade and one-of-a-kind pieces. Message sellers, arrange pickup, 
              and discover unique creations from talented Australian makers.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mt-4 text-base text-[#78716C] leading-relaxed"
            >
              <span className="font-semibold text-teal-600">For Makers & Restorers:</span> Showcase your craft with before & after photos. Share your passion, connect with buyers who appreciate handmade quality.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-4 text-base text-[#78716C] leading-relaxed"
            >
              <span className="font-semibold text-teal-600">For Trades:</span> Offer your expertise to your local area and surroundings. Connect with customers looking for skilled tradespeople they can trust.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link to="/register" data-testid="get-started-btn">
                <Button size="lg" className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide uppercase btn-active">
                  Get Started Free
                </Button>
              </Link>
              <Link to="/browse" data-testid="browse-btn">
                <Button size="lg" variant="outline" className="border-teal-500 text-teal-600 hover:bg-teal-50 font-medium tracking-wide uppercase">
                  <Search className="h-4 w-4 mr-2" />
                  Browse Items
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Showcase Your Craft
              </h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Display before & after photos of your restoration projects and handmade creations.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Direct Communication
              </h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Message sellers instantly and arrange local pickup at your convenience.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-6"
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
                <Shield className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                No Commission Fees
              </h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Keep 100% of your sales. Sellers pay only a simple subscription after a 30-day free trial.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-2">Browse by</p>
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Categories
              </h2>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {categories.map((cat, idx) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link
                  to={`/browse?category=${cat.id}`}
                  className="block p-4 bg-white border border-stone-200 hover:border-teal-300 hover:shadow-md transition-all text-center group"
                  data-testid={`category-${cat.id}`}
                >
                  <span className="text-2xl mb-2 block">{categoryIcons[cat.id] || '📦'}</span>
                  <span className="text-sm font-medium text-stone-700 group-hover:text-teal-600 transition-colors">
                    {cat.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Listings Section */}
      <section className="py-16 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-2">Latest</p>
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Featured Items
              </h2>
            </div>
            <Link to="/browse" className="text-teal-600 hover:text-teal-700 font-medium flex items-center gap-2">
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-stone-100 animate-pulse aspect-[4/5] rounded-sm" />
              ))}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredListings.slice(0, 8).map((listing, idx) => (
                <ListingCard key={listing.id} listing={listing} index={idx} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-stone-50 rounded-sm">
              <p className="text-stone-500 mb-4">No listings yet. Be the first to sell!</p>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white">
                  Start Selling
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Featured Sellers Carousel */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-yellow-50 border-t border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-2">Spotlight</p>
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Star className="inline h-7 w-7 mr-2 text-amber-500 fill-amber-500" />
                Featured Sellers
              </h2>
              <p className="text-stone-600 mt-2">Discover talented makers handpicked by our team</p>
            </div>
            <Link to="/browse" className="text-amber-700 hover:text-amber-800 font-medium flex items-center gap-2">
              Browse all sellers
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <FeaturedCarousel type="sellers" />
        </div>
      </section>

      {/* Creator Feed Section */}
      {creatorPosts.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-teal-50 to-orange-50 border-t border-teal-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 mb-2">Behind the Scenes</p>
                <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Video className="inline h-7 w-7 mr-2 text-teal-600" />
                  Creator Feed
                </h2>
                <p className="text-stone-600 mt-2">Watch how our talented makers create their masterpieces</p>
              </div>
              <Link to="/creator" className="text-teal-600 hover:text-teal-700 font-medium flex items-center gap-2">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {creatorPosts.map((post, idx) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    to="/creator"
                    className="block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="relative aspect-video bg-stone-900">
                      {post.thumbnail_url ? (
                        <img 
                          src={post.thumbnail_url.startsWith('/api') ? `${API_URL}${post.thumbnail_url}` : post.thumbnail_url}
                          alt={post.caption}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-800 to-stone-900">
                          <Video className="h-12 w-12 text-stone-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="h-6 w-6 text-teal-600 ml-0.5" fill="currentColor" />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-stone-800 line-clamp-1">{post.creator_name}</p>
                      <p className="text-xs text-stone-500 line-clamp-1">{post.caption}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/creator">
                <Button variant="outline" className="border-teal-500 text-teal-600 hover:bg-teal-50">
                  <Video className="h-4 w-4 mr-2" />
                  Explore Creator Feed
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#1C1917]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Join Our Community?
          </h2>
          <p className="text-stone-400 mb-8 max-w-xl mx-auto">
            Sign up free to browse and buy. Want to sell? Start your 30-day free trial anytime.
          </p>
          <Link to="/register" data-testid="cta-signup-btn">
            <Button size="lg" className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide uppercase">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Tradie Carousel */}
      <TradieCarousel />

      {/* Footer */}
      <footer className="bg-white border-t border-stone-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Crafters Market
              </span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm text-stone-500">
              <Link to="/browse" className="hover:text-teal-600 transition-colors">Browse</Link>
              <Link to="/subscription" className="hover:text-teal-600 transition-colors">Pricing</Link>
            </nav>
            <p className="text-sm text-stone-400">
              © {new Date().getFullYear()} Crafters Market. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
